import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import { PriorAuthGateOutcomeT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { AgentTweakableArea } from '@/features/copilot/agent-tweakable';
import { useProposedActionMutations } from '@/features/proposed-actions-plan/model/useProposedActionMutations';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { analytics, ANALYTICS_EVENTS, isRevisionFlow, logger } from '@/shared/lib';
import { pushZodIssuesToTanStackForm } from '@/shared/lib/forms';
import { getTodayYYYYMMDD } from '@/shared/lib/dates';
import {
  patientPharmaciesQueryDefinition,
  patientPharmacyToRxPharmacy,
  selectMedicationQueryDefinition,
  usePreviewPriorAuthQuery,
} from '@/entities/rx';
import { closeCopilotOverlay } from '@/shared/stores/ui';
import { useRxWizardAgentIntegration } from '../agent';
import { applySelectedMedicationToForm } from '../model/applySelectedMedicationToForm';
import { chooseDefaultPatientPharmacy } from '../model/chooseDefaultPatientPharmacy';

import type { RxWizardStepId } from '../model/types';
import { useRxWizardForm } from '../model/useRxWizardForm';
import { usePriorAuthBannerModel } from '../model/usePriorAuthBannerModel';
import type { RxSendDisposition } from '../model/submission/orchestrator';
import { useRxSubmissionOrchestrator } from '../model/submission/orchestrator';
import type { RxWizardPrefill } from '../model/rxWizardPrefill';
import { RxWizardProgress } from './RxWizardProgress';
import { DetailsStep } from './steps/DetailsStep';
import { ReviewStep } from './steps/ReviewStep';
import { ConfirmationStep } from './steps/ConfirmationStep';

/**
 * Wizard step titles. We swap the confirmation title based on the preview
 * PA outcome: a ``PA_REQUIRED`` draft never actually hits the pharmacy —
 * backend ``send_prescriptions`` diverts it into ``POST /priorAuths/initiate``
 * — so saying "Prescription sent" would be a lie. See
 * :func:`prescription_pa_gate.handle_divert`.
 */
const WIZARD_TITLE_BY_STEP: Record<RxWizardStepId, string> = {
  details: 'Prescription details',
  review: 'Prescription',
  confirmation: 'Prescription sent',
};
const PA_CONFIRMATION_TITLE = 'Prior auth sent';
const EXCLUDED_CONFIRMATION_TITLE = 'Prescription signed';

/**
 * Fire the appropriate success toast for a completed send. We branch on the
 * backend-reported disposition rather than the pre-submit preview so the
 * message always tells the truth about what happened — including surfacing
 * the PA case number when DoseSpot echoed one back on ``/priorAuths/initiate``.
 */
function showSendSuccessToast(disposition: RxSendDisposition | null): void {
  if (disposition?.kind === 'prior-auth-initiated') {
    toast.success('Prior auth sent.', {
      description: disposition.caseNumber ? `Case #${disposition.caseNumber}.` : undefined,
    });
    return;
  }
  if (disposition?.kind === 'excluded') {
    toast.warning('Prescription needs clinician action.', {
      description: 'Drug is excluded from the payer formulary.',
    });
    return;
  }
  if (disposition?.kind === 'divert-failed') {
    toast.warning('Prescription submitted with warnings.', {
      description: disposition.error,
    });
    return;
  }
  toast.success('Prescription sent.');
}

const PREV_STEP: Partial<Record<RxWizardStepId, RxWizardStepId>> = {
  review: 'details',
  confirmation: 'review',
};

const EDIT_STEP_IDS: ReadonlySet<RxWizardStepId> = new Set(['details']);

interface RxWizardProps {
  userId: string;
  prefill: RxWizardPrefill | null;
}

function RxWizardInner({ userId, prefill }: RxWizardProps) {
  const { form, resetForKind, validateDraft, buildRequest } = useRxWizardForm();
  const [stepId, setStepId] = useState<RxWizardStepId>('details');
  const [formError, setFormError] = useState<string | null>(null);
  const [paExpedited, setPaExpedited] = useState(false);
  const [lastSubmitMode, setLastSubmitMode] = useState<'send' | 'pa'>('send');
  // Optional override that pins the formulary lookup to a specific
  // ``PatientEligibilityId`` instead of letting the server pick the
  // first active coverage. Lives outside the form draft because it's
  // not part of the prescription request — it just steers the lookup
  // shown in the Details / Review steps. ``null`` means "auto".
  const [formularyEligibilityOverride, setFormularyEligibilityOverride] = useState<number | null>(
    null
  );

  const orchestrator = useRxSubmissionOrchestrator();
  const {
    state: submissionState,
    isDraftLocked,
    isSubmitting,
    reset: resetSubmission,
  } = orchestrator;
  const mutations = useProposedActionMutations();

  useRxWizardAgentIntegration(form);
  const queryClient = useQueryClient();
  // ``prefill`` arrives as a prop (the overlay carries it and remounts on each
  // open). It's consumed once by the hydration effect below.
  const sourceProposedActionRef = useRef<NonNullable<
    RxWizardPrefill['sourceProposedAction']
  > | null>(null);
  const existingRxIdRef = useRef<string | null>(null);
  const lastAutoDispenseUnitIdRef = useRef<string | null>(null);
  const [isEditableMode, setIsEditableMode] = useState(true);
  const [isHydrating, setIsHydrating] = useState(false);

  // Preview PA outcome — the `ReviewStep` already calls the same hook, so
  // react-query dedupes this to a single in-flight request. This drives
  // *pre-submit* copy only (button label, "Sending PA…" spinner) where we
  // haven't yet heard back from the server. Post-submit copy reads from the
  // backend's actual send response via ``submissionState.sendDisposition``
  // so the toast / confirmation always tell the truth — even when the
  // Rx-time gate disagrees with the preview (e.g. formulary data moved
  // between preview and send).
  const priorAuthParams = usePriorAuthBannerModel(form.state.values, formularyEligibilityOverride);
  const priorAuthPreviewQuery = usePreviewPriorAuthQuery({
    userId,
    params: priorAuthParams,
  });
  // Both ``PA_REQUIRED`` and ``EXCLUDED`` divert the Rx away from the
  // pharmacy — the former auto-initiates a PA case, the latter opens a
  // clinician-action request. ``UNKNOWN`` default-allows through.
  const previewOutcome = priorAuthPreviewQuery.data?.outcome ?? null;
  const willDivert =
    previewOutcome === PriorAuthGateOutcomeT.PA_REQUIRED ||
    previewOutcome === PriorAuthGateOutcomeT.EXCLUDED;
  const isPriorAuthPreview = previewOutcome === PriorAuthGateOutcomeT.PA_REQUIRED;
  const isExcludedPreview = previewOutcome === PriorAuthGateOutcomeT.EXCLUDED;

  // After a completed send, the server tells us exactly what happened. Use
  // that when it's available; otherwise fall back to the preview signal so
  // the button labels and the "Sending PA…" spinner still match the best
  // information we have pre-submit.
  const sendDisposition = submissionState.sendDisposition;
  const isPriorAuthResolved = sendDisposition
    ? sendDisposition.kind === 'prior-auth-initiated'
    : isPriorAuthPreview;
  const isExcludedResolved = sendDisposition
    ? sendDisposition.kind === 'excluded'
    : isExcludedPreview;
  const wasDiverted = isPriorAuthResolved || isExcludedResolved;

  const reportProposedActionSuccess = useCallback(() => {
    const source = sourceProposedActionRef.current;
    if (!source) return;
    mutations.finalizeFromSource(source).catch((err: unknown) => {
      logger.error('rxWizard.finalizeFromSource.failed', {
        externalId: source.externalId,
        err,
      });
    });
  }, [mutations]);

  const analyticsContext = useMemo(
    () => ({ actor: 'clinician' as const, patientUserId: userId }),
    [userId]
  );

  const wizardOpenedRef = useRef(false);
  useEffect(() => {
    if (wizardOpenedRef.current) return;
    wizardOpenedRef.current = true;
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.wizardOpened,
      context: analyticsContext,
    });
  }, [analyticsContext]);

  const prevStepRef = useRef(stepId);
  useEffect(() => {
    if (prevStepRef.current === stepId) return;
    prevStepRef.current = stepId;
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.wizardStepViewed,
      context: analyticsContext,
      properties: { stepId },
    });
  }, [stepId, analyticsContext]);

  useEffect(() => {
    if (!prefill) {
      return;
    }
    if (isDraftLocked) {
      return;
    }

    let cancelled = false;
    setIsHydrating(true);
    void (async () => {
      let medicationFailed = false;
      let pharmacyFailed = false;
      try {
        if (prefill.sourceProposedAction) {
          sourceProposedActionRef.current = prefill.sourceProposedAction;
        }
        if (prefill.existingDosespotPrescriptionId) {
          existingRxIdRef.current = prefill.existingDosespotPrescriptionId;
        }

        const isRevision = isRevisionFlow(prefill.sourceProposedAction);
        const isExecuteOnly =
          (!!prefill.sourceProposedAction && !isRevision) ||
          !!prefill.existingDosespotPrescriptionId;
        setIsEditableMode(!isExecuteOnly);

        resetSubmission();
        resetForKind('coded');
        setFormError(null);
        setStepId(isRevision ? 'details' : 'review');

        // Apply scalar prefill fields immediately so they are never blocked by
        // async medication/pharmacy lookups.
        form.setFieldValue('directions', prefill.directions);
        form.setFieldValue('quantity', prefill.quantity);
        form.setFieldValue('refills', prefill.refills);
        form.setFieldValue('daysSupply', prefill.daysSupply);
        form.setFieldValue('pharmacyNotes', prefill.pharmacyNotes ?? '');
        form.setFieldValue('noSubstitutions', prefill.noSubstitutions ?? false);
        form.setFieldValue('effectiveDate', prefill.effectiveDate ?? getTodayYYYYMMDD());

        try {
          const selectedMedication = await queryClient.fetchQuery(
            selectMedicationQueryDefinition({ dispensable_drug_id: prefill.dispensableDrugId })
          );
          if (!cancelled) {
            applySelectedMedicationToForm({
              form,
              selected: selectedMedication,
              lastAutoDispenseUnitIdRef,
            });
          }
        } catch (err: unknown) {
          medicationFailed = true;
          logger.warn('rxWizard.prefill.medicationLookupFailed', {
            dispensableDrugId: prefill.dispensableDrugId,
            err,
          });
        }

        try {
          const patientPharmacies = await queryClient.fetchQuery(
            patientPharmaciesQueryDefinition(userId)
          );
          const defaultPatientPharmacy = chooseDefaultPatientPharmacy(patientPharmacies);
          const selectedPharmacy = defaultPatientPharmacy
            ? patientPharmacyToRxPharmacy(defaultPatientPharmacy)
            : null;
          if (selectedPharmacy) {
            if (!cancelled) {
              form.setFieldValue('selectedPharmacy', selectedPharmacy);
            }
          } else {
            pharmacyFailed = true;
            logger.warn('rxWizard.prefill.noDefaultPharmacy', { userId });
          }
        } catch (err: unknown) {
          pharmacyFailed = true;
          logger.warn('rxWizard.prefill.defaultPharmacyLookupFailed', { userId, err });
        }

        // In execute-only mode, reject the proposed action when required data
        // could not be resolved — the agent produced an un-executable action.
        // When re-sending an existing DoseSpot prescription the medication and
        // pharmacy are already embedded in that prescription, so local lookup
        // failures are non-blocking.
        const hasExistingRx = !!prefill.existingDosespotPrescriptionId;
        if (!cancelled && isExecuteOnly && !hasExistingRx && (medicationFailed || pharmacyFailed)) {
          const failures: string[] = [];
          if (medicationFailed) failures.push('medication lookup failed');
          if (pharmacyFailed) failures.push('no default pharmacy found for patient');

          const reason = `Could not prepare prescription: ${failures.join('; ')}`;
          const { externalId } = prefill.sourceProposedAction!;

          toast.error('Prescription action could not be executed.', {
            description: reason,
          });

          try {
            await mutations.reject(externalId, reason);
          } catch (rejectErr: unknown) {
            logger.error('rxWizard.prefill.autoRejectFailed', { externalId, rejectErr });
          }

          closeCopilotOverlay('rxWizard');
          return;
        }
      } finally {
        if (!cancelled) {
          setIsHydrating(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form, isDraftLocked, mutations, resetSubmission, prefill, queryClient, resetForKind, userId]);

  const validateDetailsAndStayOrAdvance = async () => {
    setFormError(null);

    // Populate TanStack Form errors based on validators:
    await form.validateAllFields('submit');

    const result = validateDraft();
    if (result.success) {
      setStepId('review');
      return;
    }

    // Push Zod errors into TanStack Form field meta so adapters display them.
    pushZodIssuesToTanStackForm(form, result.error.issues);

    const topLevel = result.error.issues.find((i) => i.path?.[0] === '_form')?.message;
    setFormError(topLevel ?? 'Fix validation errors before continuing.');
    setStepId('details');
  };

  const handleBack = () => {
    const prev =
      stepId === 'confirmation' && submissionState.status === 'failed-create'
        ? 'review'
        : (PREV_STEP[stepId] ?? 'details');
    if (isDraftLocked && EDIT_STEP_IDS.has(prev)) return;
    setFormError(null);
    setStepId(prev);
  };

  const handleNext = async () => {
    if (stepId === 'details') {
      await validateDetailsAndStayOrAdvance();
    }
  };

  const handleSubmit = async (mode: 'send' | 'pa' = 'send') => {
    setLastSubmitMode(mode);
    setFormError(null);
    await form.validateAllFields('submit');

    const result = buildRequest();
    if (!result.success) {
      pushZodIssuesToTanStackForm(form, result.error.issues);
      setFormError(
        isEditableMode
          ? 'Fix validation errors before submitting.'
          : 'Prescription details could not be fully loaded for this execute-only action.'
      );
      if (isEditableMode) {
        setStepId('details');
      }
      return;
    }

    const rxKind = form.state.values.kind;
    void analytics.track({
      name: ANALYTICS_EVENTS.rx.submitAttempted,
      context: analyticsContext,
      properties: {
        kind: rxKind,
        hasPharmacyNotes: (form.state.values.pharmacyNotes ?? '').trim().length > 0,
        noSubstitutions: form.state.values.noSubstitutions,
      },
    });

    const existingRxId = existingRxIdRef.current;

    let dosespotPrescriptionId: string;
    if (existingRxId) {
      dosespotPrescriptionId = existingRxId;
    } else {
      const diag = form.state.values.selectedDiagnosis;
      const created = await orchestrator.create({
        userId,
        rxRequest: result.data,
        diagnosis: diag
          ? {
              diagnosisId: diag.diagnosisId,
              diagnosisCode: diag.code,
              diagnosisDescription: diag.name,
            }
          : undefined,
      });
      if (!created) {
        toast.error('Could not create prescription.', {
          description: orchestrator.state.errorMessage ?? undefined,
        });
        void analytics.track({
          name: ANALYTICS_EVENTS.rx.submitResult,
          context: analyticsContext,
          properties: { kind: rxKind, outcome: 'failure' },
        });
        setStepId('confirmation');
        return;
      }
      dosespotPrescriptionId = created.dosespotPrescriptionId;
    }

    const isPaMode = mode === 'pa';
    const disposition = await orchestrator.send({
      userId,
      dosespotPrescriptionId,
      expedited: isPaMode ? paExpedited : undefined,
      paOnly: isPaMode || undefined,
      skipPaGate: existingRxId ? true : (!isPaMode && willDivert) || undefined,
      patientEligibilityId: formularyEligibilityOverride,
    });

    if (disposition) {
      showSendSuccessToast(disposition);
      reportProposedActionSuccess();
      closeCopilotOverlay('rxWizard');
    } else {
      toast.error(
        isPaMode ? 'Could not initiate prior authorization.' : 'Could not send prescription.',
        {
          description: orchestrator.state.errorMessage ?? undefined,
        }
      );
    }

    void analytics.track({
      name: ANALYTICS_EVENTS.rx.submitResult,
      context: analyticsContext,
      properties: { kind: rxKind, outcome: disposition !== null ? 'success' : 'failure' },
    });

    setStepId('confirmation');
  };

  const prevStepId =
    stepId === 'confirmation' && submissionState.status === 'failed-create'
      ? 'review'
      : (PREV_STEP[stepId] ?? 'details');
  const isBackDisabled =
    stepId === 'details' ||
    (isDraftLocked && EDIT_STEP_IDS.has(prevStepId)) ||
    (!isEditableMode && stepId === 'review');

  const retryCreate = async () => {
    await handleSubmit(lastSubmitMode);
  };

  const retrySend = async () => {
    const existingRxId = existingRxIdRef.current;
    const isPaMode = lastSubmitMode === 'pa';

    const disposition = existingRxId
      ? await orchestrator.send({
          userId,
          dosespotPrescriptionId: existingRxId,
          skipPaGate: true,
        })
      : await orchestrator.retrySend(userId, {
          expedited: isPaMode ? paExpedited : undefined,
          paOnly: isPaMode || undefined,
          skipPaGate: (!isPaMode && willDivert) || undefined,
          patientEligibilityId: formularyEligibilityOverride,
        });
    if (disposition) {
      showSendSuccessToast(disposition);
      reportProposedActionSuccess();
      closeCopilotOverlay('rxWizard');
    } else {
      toast.error(
        isPaMode ? 'Could not initiate prior authorization.' : 'Could not send prescription.',
        {
          description: orchestrator.state.errorMessage ?? undefined,
        }
      );
    }
  };

  return (
    <div className="w-full max-w-3xl">
      {isEditableMode ? (
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-medium leading-none tracking-tight">
            {stepId === 'confirmation' && wasDiverted
              ? isPriorAuthResolved
                ? PA_CONFIRMATION_TITLE
                : EXCLUDED_CONFIRMATION_TITLE
              : WIZARD_TITLE_BY_STEP[stepId]}
          </h2>
          <div className="pt-3">
            <RxWizardProgress stepId={stepId} />
          </div>
        </div>
      ) : null}

      <div className="mt-6 flex flex-col gap-6">
        {formError ? (
          <Alert variant="destructive">
            <AlertTitle>Can't continue</AlertTitle>
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        ) : null}

        {stepId === 'details' ? (
          <DetailsStep
            form={form}
            userId={userId}
            formularyEligibilityOverride={formularyEligibilityOverride}
            onFormularyEligibilityOverrideChange={setFormularyEligibilityOverride}
          />
        ) : null}

        {stepId === 'review' ? (
          <ReviewStep
            userId={userId}
            form={form}
            errorMessage={submissionState.errorMessage}
            onEditDetails={isEditableMode ? () => setStepId('details') : undefined}
            readOnly={!isEditableMode}
            paExpedited={paExpedited}
            onPaExpeditedChange={setPaExpedited}
            formularyEligibilityOverride={formularyEligibilityOverride}
            onFormularyEligibilityOverrideChange={setFormularyEligibilityOverride}
          />
        ) : null}

        {stepId === 'confirmation' ? (
          <ConfirmationStep
            submissionState={submissionState}
            onRetryCreate={retryCreate}
            onRetrySend={retrySend}
            isPriorAuth={isPriorAuthResolved}
            isExcluded={isExcludedResolved}
            expedited={paExpedited}
            priorAuthCaseNumber={
              sendDisposition?.kind === 'prior-auth-initiated' ? sendDisposition.caseNumber : null
            }
          />
        ) : null}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button
          type="button"
          variant="secondary"
          radius="pill"
          onClick={handleBack}
          disabled={isBackDisabled}
        >
          Back
        </Button>

        <div className="flex gap-2">
          {stepId === 'review' ? (
            <>
              <Button
                type="button"
                radius="pill"
                onClick={() => void handleSubmit('send')}
                disabled={isSubmitting || isDraftLocked || isHydrating}
                variant={existingRxIdRef.current ? 'default' : willDivert ? 'secondary' : 'default'}
              >
                {isHydrating
                  ? 'Loading…'
                  : submissionState.status === 'creating'
                    ? 'Creating…'
                    : submissionState.status === 'sending' && lastSubmitMode === 'send'
                      ? 'Sending…'
                      : submissionState.status === 'failed-create'
                        ? 'Retry create'
                        : isDraftLocked
                          ? 'Created'
                          : existingRxIdRef.current
                            ? 'Send to pharmacy'
                            : 'Create & send'}
              </Button>
              {!existingRxIdRef.current && (
                <Button
                  type="button"
                  radius="pill"
                  variant={willDivert ? 'default' : 'secondary'}
                  onClick={() => void handleSubmit('pa')}
                  disabled={isSubmitting || isDraftLocked || isHydrating}
                >
                  {submissionState.status === 'sending' && lastSubmitMode === 'pa'
                    ? 'Creating PA…'
                    : paExpedited
                      ? 'Create PA (Expedited)'
                      : 'Create PA'}
                </Button>
              )}
            </>
          ) : stepId === 'confirmation' ? (
            <Button
              type="button"
              radius="pill"
              onClick={() => closeCopilotOverlay()}
              disabled={isSubmitting}
            >
              Finish
            </Button>
          ) : (
            <Button type="button" radius="pill" onClick={() => void handleNext()}>
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function RxWizard({ userId, prefill }: RxWizardProps) {
  return (
    <AgentTweakableArea prefix="rxWizard">
      <RxWizardInner userId={userId} prefill={prefill} />
    </AgentTweakableArea>
  );
}
